import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { NLPLogo } from '../components/nlp/NLPLogo';
import { Calendar, Clock, Video, CheckCircle, ArrowRight } from 'lucide-react';

const ZOOM_URL = 'https://us06web.zoom.us/j/86050009045?pwd=j7DC7bxl8o4NahXpLTpuRsfVxFRubO.1';

const whatToExpect = [
  'Why physical wellbeing deserves attention alongside mental and emotional change',
  "Patrick's personal health journey and what it taught him",
  'A simple 15-30 second daily wellness practice',
  'How to introduce optional wellness resources alongside coaching work',
  'Live Q&A — bring your questions',
];

export function BeyondTheMindThankYouPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-nlp-bg">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-nlp-bg/95 backdrop-blur-lg shadow-sm">
        <div className="max-w-[1216px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            <button onClick={() => navigate('/home')} className="flex items-center">
              <NLPLogo className="h-12" />
            </button>
            <Button
              onClick={() => navigate('/home')}
              variant="secondary"
              className="bg-nlp-cream text-nlp-forest border border-nlp-cream-dark hover:bg-nlp-cream-dark rounded-full"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      {/* Success Hero */}
      <section className="pt-32 pb-12 px-4 md:px-8 bg-nlp-bg relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-nlp-olive/5 rounded-full blur-3xl -translate-y-1/4 translate-x-1/4" />
        <div className="max-w-[800px] mx-auto relative text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 bg-nlp-olive/10 rounded-full flex items-center justify-center mx-auto mb-5 border border-nlp-olive/20"
          >
            <CheckCircle className="w-10 h-10 text-nlp-olive" />
          </motion.div>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-full mb-6">
            <CheckCircle className="w-4 h-4 text-nlp-olive" />
            <span className="text-sm font-semibold text-nlp-forest">You're Registered</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Beyond the Mind
          </h1>
          <p className="text-lg md:text-xl text-nlp-olive mb-3 font-semibold" style={{ fontFamily: 'Georgia, serif' }}>
            The Missing Dimension of Personal Transformation
          </p>
          <p className="text-nlp-taupe max-w-md mx-auto leading-relaxed">
            A confirmation email is on its way to you with your Zoom access link and all the event details.
          </p>
        </div>
      </section>

      {/* Event Details + Zoom */}
      <section className="py-12 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[600px] mx-auto">
          <div className="bg-nlp-bg rounded-4xl p-8 md:p-10 shadow-soft-lg">
            <h2 className="text-xl font-bold text-nlp-forest mb-6" style={{ fontFamily: 'Georgia, serif' }}>
              Event Details
            </h2>
            <div className="space-y-4 mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-nlp-cream rounded-xl flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-6 h-6 text-nlp-olive" />
                </div>
                <div>
                  <p className="text-nlp-forest font-semibold">Friday, October 23, 2026</p>
                  <p className="text-nlp-taupe text-sm">60-minute live session</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-nlp-cream rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-nlp-olive" />
                </div>
                <div>
                  <p className="text-nlp-forest font-semibold">11:00 AM US Eastern</p>
                  <p className="text-nlp-taupe text-sm">4:00 PM UK & Ireland</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-nlp-cream rounded-xl flex items-center justify-center flex-shrink-0">
                  <Video className="w-6 h-6 text-nlp-olive" />
                </div>
                <div>
                  <p className="text-nlp-forest font-semibold">Live via Zoom</p>
                  <p className="text-nlp-taupe text-sm">Link sent to your email — check your inbox</p>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-nlp-cream-dark">
              <a
                href={ZOOM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-nlp-forest hover:bg-nlp-forest-light text-white rounded-full font-semibold transition-colors"
              >
                <Video className="w-5 h-5" />
                Join the Live Session
                <ArrowRight className="w-5 h-5" />
              </a>
              <p className="text-nlp-taupe text-sm text-center mt-4">
                The Zoom link above will be active at the scheduled time. You can also find it in your confirmation email.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[600px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-nlp-forest mb-2" style={{ fontFamily: 'Georgia, serif' }}>
              What to Expect
            </h2>
          </div>
          <div className="space-y-3">
            {whatToExpect.map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-nlp-cream rounded-2xl p-4">
                <CheckCircle className="w-5 h-5 text-nlp-olive flex-shrink-0 mt-0.5" />
                <span className="text-nlp-forest font-medium text-sm leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Presenter */}
      <section className="py-12 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[600px] mx-auto text-center">
          <img
            src="/Patrick_Profile_Under_2MB_q95.jpg"
            alt="Patrick Dahdal"
            className="w-16 h-16 rounded-full object-cover border-2 border-nlp-gold/30 mx-auto mb-4"
          />
          <p className="text-nlp-forest font-bold text-lg" style={{ fontFamily: 'Georgia, serif' }}>Patrick Dahdal</p>
          <p className="text-nlp-taupe text-sm">Personal Transformation & Wellbeing</p>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[700px] mx-auto">
          <p className="text-sm text-nlp-taupe leading-relaxed text-center">
            This is an educational session based on personal experience and general wellness approaches. Patrick is an independent LifeWave Brand Partner. Products may be discussed and offered at the event. It is not medical advice, and wellness products are not intended to diagnose, treat, cure, or prevent disease.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 md:px-8 bg-nlp-forest-dark text-nlp-bg/60">
        <div className="max-w-[1216px] mx-auto text-center">
          <NLPLogo className="h-10 mx-auto mb-4" />
          <p className="text-sm">NLP OS — Personal Transformation & Wellbeing</p>
        </div>
      </footer>
    </div>
  );
}
